import { useCallback, useEffect, useState, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Action from 'supportAssistant/components/Chat/MessageMeta/Action'
import { isChip } from 'supportAssistant/utils/actionsUtils'
import { getPredictedWelcomeChips } from 'api/supportAssistant'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineLightbulbAltIcon from 'LuxKit/Icons/line/LineLightbulbAltIcon'
import LinePenIcon from 'LuxKit/Icons/line/LinePenIcon'
import { fillArray } from 'lib/array/arrayUtils'
import { trackClientEvent } from 'analytics/analytics'

const PREDICTED_CHIP_SKELETON_INDEXES = fillArray(3)
const PREDICTED_CHIPS_RETRY_MS = 15000
const PEN_CHIP_CATEGORIES = new Set(['specialRequest', 'changeGuestName'])

const ActionsContainer = styled.div`
  width: 100%;
  max-width: ${rem(720)};
`

interface Props {
  payload?: App.ChatMessageMetaPayload
}

function LesaPredictedWelcomeChips(props: Props) {
  const { payload } = props
  const intl = useIntl()
  const [predictedChips, setPredictedChips] = useState<Array<App.ChatMessageMetaContent>>([])
  const [predictedLoading, setPredictedLoading] = useState(true)

  const bookingLabel = intl.formatMessage({
    id: 'BJrkGDe23Q1fi7PrxgIw',
    defaultMessage: 'Booking',
    description: 'Category label shown on predicted LESA welcome chips that start an order or booking edit',
  })
  const inspirationsHeading = intl.formatMessage({
    id: 'uy5mRZPXuSpiz_ekm3tm',
    defaultMessage: 'Inspirations for you',
    description: 'Heading shown above predicted LESA welcome chips on the empty support chat screen',
  })
  const chipLabel = (category?: string) => (
    <CaptionTextBlock
      variant="medium"
      weight="bold"
      colour="highlight-primary-dark"
      startIcon={PEN_CHIP_CATEGORIES.has(category ?? '')
        ? <LinePenIcon size="XS" colour="inherit" />
        : <LineCalendarIcon size="XS" colour="inherit" />}
    >
      {bookingLabel}
    </CaptionTextBlock>
  )

  const loadPredictedChips = useCallback((lastAttempt = false) => {
    return getPredictedWelcomeChips().then((content) => {
      setPredictedChips((current) => content.length ? content : current)
      if (lastAttempt || content.length) setPredictedLoading(false)
    }).catch(() => {
      if (lastAttempt) setPredictedLoading(false)
    })
  }, [])

  useEffect(() => {
    let cancelled = false
    setPredictedLoading(true)
    loadPredictedChips()
    const timeout = setTimeout(() => {
      if (!cancelled) loadPredictedChips(true)
    }, PREDICTED_CHIPS_RETRY_MS)
    return () => { cancelled = true; clearTimeout(timeout) }
  }, [loadPredictedChips])

  const predictedChipActions = predictedChips.filter(action => isChip(action) && action.action?.key)

  const handlePredictedChipClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    trackClientEvent({
      subject: 'support_assistant',
      action: `lesa-empty-chat-predicted-pill-clicked_${event.currentTarget.dataset.actionKey ?? 'unknown'}`,
      category: 'logging',
      type: 'interaction',
    })
  }, [])

  if (!predictedLoading && predictedChipActions.length < 2) return null

  return (
    <Group direction="vertical" gap={16} horizontalAlign="center" fullWidth>
      <BodyTextBlock variant="medium" weight="bold" colour="light-foreground-alt" startIcon={<LineLightbulbAltIcon size="S" />}>
        {inspirationsHeading}
      </BodyTextBlock>
      <ActionsContainer>
        <Group
          direction="horizontal"
          tabletDirection="horizontal"
          wrap="wrap"
          horizontalAlign="center"
          verticalAlign="center"
          gap={12}
          fullWidth
        >
          {predictedLoading && PREDICTED_CHIP_SKELETON_INDEXES.map(index => (
            <LoadingBox key={index} width={200} height={96} borderRadius="XL" />
          ))}
          {!predictedLoading && predictedChipActions.map((action) => (
            <div
              key={`${action.action?.key}-${action.text}`}
              data-action-key={action.action?.key}
              onClick={handlePredictedChipClick}
            >
              <Action
                action={action}
                payload={payload}
                label={chipLabel(action.category)}
              />
            </div>
          ))}
        </Group>
      </ActionsContainer>
    </Group>
  )
}

export default LesaPredictedWelcomeChips
